import api from '@/lib/api';
import type {
  UserProfile, UserInvite, Tenant, UserRole,
  EmailConfig, EmailSendLog, EmailEventType,
  VapiConfig, VapiCallLog,
  IntegrationStatus, IntegrationProvider,
  Site, Customer,
} from '@/types/models';

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true' || !import.meta.env.VITE_API_URL;

function unwrapData<T>(payload: unknown): T | undefined {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    return (payload as { data?: T }).data;
  }

  return payload as T;
}

function unwrapCollection<T>(payload: unknown): T[] {
  const unwrapped = unwrapData<unknown>(payload);

  if (Array.isArray(unwrapped)) {
    return unwrapped as T[];
  }

  if (unwrapped && typeof unwrapped === 'object') {
    const firstArray = Object.values(unwrapped as Record<string, unknown>).find(Array.isArray);
    if (Array.isArray(firstArray)) {
      return firstArray as T[];
    }
  }

  return [];
}

function unwrapResource<T>(payload: unknown): T {
  const unwrapped = unwrapData<unknown>(payload);

  if (unwrapped && typeof unwrapped === 'object' && !Array.isArray(unwrapped)) {
    const values = Object.values(unwrapped as Record<string, unknown>);
    const [firstValue] = values;

    if (values.length === 1 && firstValue && typeof firstValue === 'object' && !Array.isArray(firstValue)) {
      return firstValue as T;
    }
  }

  return (unwrapped ?? payload) as T;
}

function normalizeUserRole(role: unknown): UserRole | null {
  if (typeof role === 'string' && role.length > 0) {
    return role as UserRole;
  }

  if (role && typeof role === 'object') {
    const roleRecord = role as Record<string, unknown>;
    const namedRole = roleRecord.name ?? roleRecord.role ?? roleRecord.slug;

    if (typeof namedRole === 'string' && namedRole.length > 0) {
      return namedRole as UserRole;
    }
  }

  return null;
}

function normalizeUserProfile(user: unknown): UserProfile {
  const record = (user && typeof user === 'object' ? user : {}) as Record<string, unknown>;
  const rawRoles = record.roles;
  const rolesSource = Array.isArray(rawRoles)
    ? rawRoles
    : rawRoles && typeof rawRoles === 'object' && Array.isArray((rawRoles as Record<string, unknown>).data)
      ? ((rawRoles as Record<string, unknown>).data as unknown[])
      : [];

  return {
    ...(record as unknown as UserProfile),
    roles: rolesSource.map(normalizeUserRole).filter((role): role is UserRole => role !== null),
    is_super_admin: Boolean(record.is_super_admin),
    is_active: record.is_active === undefined ? true : Boolean(record.is_active),
  };
}

function normalizeUserInvite(invite: unknown): UserInvite {
  const record = (invite && typeof invite === 'object' ? invite : {}) as Record<string, unknown>;

  return {
    ...(record as unknown as UserInvite),
    accepted_at: typeof record.accepted_at === 'string' ? record.accepted_at : undefined,
  };
}

// ── Mock Data ──────────────────────────────────────────

const MOCK_USERS: UserProfile[] = [
  {
    id: 'dev-1', tenant_id: 'tenant-1', name: 'Dev User', email: 'dev@charmcrush.com',
    roles: ['admin'], is_super_admin: true, is_active: true,
    last_login_at: '2025-03-30T10:00:00Z',
    created_at: '2025-01-01T00:00:00Z', updated_at: '2025-03-30T10:00:00Z',
  },
  {
    id: 'user-2', tenant_id: 'tenant-1', name: 'Jane Operator', email: 'jane@ecotech.com',
    roles: ['operator', 'intake_staff'], is_super_admin: false, is_active: true,
    last_login_at: '2025-03-29T08:00:00Z',
    created_at: '2025-02-15T00:00:00Z', updated_at: '2025-03-29T08:00:00Z',
  },
  {
    id: 'user-3', tenant_id: 'tenant-1', name: 'Tom Driver', email: 'tom@ecotech.com',
    roles: ['driver'], is_super_admin: false, is_active: true,
    created_at: '2025-03-01T00:00:00Z', updated_at: '2025-03-01T00:00:00Z',
  },
];

const MOCK_TENANTS: Tenant[] = [
  {
    id: 'tenant-1', name: 'EcoTech Recycling', slug: 'ecotech',
    features: { crm_enabled: true, vapi_enabled: false },
    is_active: true, created_at: '2025-01-01T00:00:00Z', updated_at: '2025-03-30T00:00:00Z',
  },
  {
    id: 'tenant-2', name: 'GreenCycle Solutions', slug: 'greencycle',
    features: { crm_enabled: false, vapi_enabled: false },
    is_active: true, created_at: '2025-02-01T00:00:00Z', updated_at: '2025-03-15T00:00:00Z',
  },
  {
    id: 'tenant-3', name: 'ReUse Partners', slug: 'reuse',
    features: { crm_enabled: true, vapi_enabled: true },
    is_active: false, created_at: '2025-01-15T00:00:00Z', updated_at: '2025-03-01T00:00:00Z',
  },
];

const MOCK_INVITES: UserInvite[] = [
  {
    id: 'inv-1', tenant_id: 'tenant-1', email: 'newguy@ecotech.com', role: 'operator' as UserRole,
    token: 'abc123', expires_at: '2025-04-15T00:00:00Z',
    created_at: '2025-03-28T00:00:00Z',
  },
];

const MOCK_EMAIL_CONFIG: EmailConfig = {
  id: 'ec-1', tenant_id: 'tenant-1', sender_name: 'EcoTech Recycling', reply_to_email: 'support@ecotech.com',
  notifications: {
    work_order_created: true, intake_complete: true, processing_complete: false,
    shipment_created: true, destruction_certificate: true,
  },
  created_at: '2025-01-01T00:00:00Z', updated_at: '2025-03-30T00:00:00Z',
};

const MOCK_EMAIL_LOG: EmailSendLog[] = [
  { id: 'el-1', tenant_id: 'tenant-1', recipient: 'client@acme.com', subject: 'Work Order #WO-2025-042 Created', event_type: 'work_order_created', status: 'sent', sent_at: '2025-03-30T10:05:00Z' },
  { id: 'el-2', tenant_id: 'tenant-1', recipient: 'jane@bigcorp.com', subject: 'Intake Complete — WO-2025-041', event_type: 'intake_complete', status: 'sent', sent_at: '2025-03-29T15:22:00Z' },
  { id: 'el-3', tenant_id: 'tenant-1', recipient: 'bob@test.com', subject: 'Destruction Certificate Ready', event_type: 'destruction_certificate', status: 'failed', sent_at: '2025-03-28T09:00:00Z' },
];

const MOCK_VAPI_CONFIG: VapiConfig = {
  id: 'vc-1', tenant_id: 'tenant-1', api_key_masked: '••••••••abc123',
  default_greeting: 'Hello! This is EcoTech Recycling calling about your upcoming pickup.',
  voice_id: 'rachel', max_call_duration: 300, is_active: true,
  created_at: '2025-02-01T00:00:00Z', updated_at: '2025-03-20T00:00:00Z',
};

const MOCK_VAPI_CALLS: VapiCallLog[] = [
  { id: 'vcl-1', tenant_id: 'tenant-1', contact_id: 'c-1', contact_name: 'John Smith', direction: 'outbound', duration_seconds: 142, outcome: 'Confirmed pickup', transcript_preview: 'Hi John, this is EcoTech calling to confirm...', created_at: '2025-03-30T11:00:00Z' },
  { id: 'vcl-2', tenant_id: 'tenant-1', contact_id: 'c-2', contact_name: 'Sarah Lee', direction: 'outbound', duration_seconds: 68, outcome: 'Voicemail', transcript_preview: 'Hi Sarah, we wanted to follow up on...', created_at: '2025-03-29T14:30:00Z' },
];

const MOCK_INTEGRATIONS: IntegrationStatus[] = [
  { provider: 'twilio', display_name: 'Twilio', description: 'SMS messaging and voice calls', is_active: true, is_configured: true, last_verified_at: '2025-03-29T00:00:00Z' },
  { provider: 'hunter', display_name: 'Hunter.io', description: 'Email finding and lead enrichment', is_active: false, is_configured: false },
  { provider: 'docusign', display_name: 'DocuSign', description: 'Electronic document signing', is_active: false, is_configured: false },
  { provider: 'aws_s3', display_name: 'AWS S3', description: 'File and document storage', is_active: true, is_configured: true, last_verified_at: '2025-03-30T00:00:00Z' },
];

const MOCK_SITES: Site[] = [
  { id: 'site-1', name: 'Main Warehouse', address: '123 Industrial Blvd', city: 'Denver', state: 'CO', is_active: true },
  { id: 'site-2', name: 'Processing Center', address: '456 Recycle Dr', city: 'Boulder', state: 'CO', is_active: true },
  { id: 'site-3', name: 'Satellite Office', address: '789 Tech Ave', city: 'Colorado Springs', state: 'CO', is_active: false },
];

const MOCK_CUSTOMERS: Customer[] = [
  { id: 'cust-1', name: 'Acme Corp', company: 'Acme Corp', is_active: true },
  { id: 'cust-2', name: 'BigCorp Inc', company: 'BigCorp Inc', is_active: true },
  { id: 'cust-3', name: 'TechStart LLC', company: 'TechStart LLC', is_active: false },
];

// ── Service ────────────────────────────────────────────

export const adminService = {
  // Users
  async getUsers(): Promise<UserProfile[]> {
    if (USE_MOCK) return MOCK_USERS;
    const res = await api.get('/api/users');
    return unwrapCollection<UserProfile>(res.data).map(normalizeUserProfile);
  },
  async getUser(id: string): Promise<UserProfile> {
    if (USE_MOCK) return MOCK_USERS.find(u => u.id === id)!;
    const res = await api.get(`/api/users/${id}`);
    return normalizeUserProfile(unwrapResource<UserProfile>(res.data));
  },
  async updateUser(id: string, data: Partial<UserProfile>): Promise<UserProfile> {
    if (USE_MOCK) return { ...MOCK_USERS.find(u => u.id === id)!, ...data };
    const res = await api.patch(`/api/users/${id}`, data);
    return normalizeUserProfile(unwrapResource<UserProfile>(res.data));
  },
  async deactivateUser(id: string): Promise<void> {
    if (USE_MOCK) return;
    await api.delete(`/api/users/${id}`);
  },

  // Invites
  async sendInvite(data: { email: string; role: UserRole; tenant_id?: string; roles?: UserRole[] }): Promise<UserInvite> {
    if (USE_MOCK) return { ...MOCK_INVITES[0], ...data, id: `inv-${Date.now()}` } as UserInvite;
    const res = await api.post('/api/users/invite', data);
    return normalizeUserInvite(unwrapResource<UserInvite>(res.data));
  },
  async getInvites(): Promise<UserInvite[]> {
    if (USE_MOCK) return MOCK_INVITES;
    const res = await api.get('/api/users/invites');
    return unwrapCollection<UserInvite>(res.data).map(normalizeUserInvite);
  },

  // Tenants (super admin)
  async getTenants(): Promise<Tenant[]> {
    if (USE_MOCK) return MOCK_TENANTS;
    const res = await api.get('/api/tenants');
    return unwrapCollection<Tenant>(res.data);
  },
  async getTenant(id: string): Promise<Tenant> {
    if (USE_MOCK) return MOCK_TENANTS.find(t => t.id === id)!;
    const res = await api.get(`/api/tenants/${id}`);
    return unwrapResource<Tenant>(res.data);
  },
  async updateTenant(id: string, data: Partial<Tenant>): Promise<Tenant> {
    if (USE_MOCK) return { ...MOCK_TENANTS.find(t => t.id === id)!, ...data };
    const res = await api.patch(`/api/tenants/${id}`, data);
    return unwrapResource<Tenant>(res.data);
  },
  async createTenant(data: Partial<Tenant>): Promise<Tenant> {
    if (USE_MOCK) return { ...MOCK_TENANTS[0], ...data, id: `tenant-${Date.now()}` } as Tenant;
    const res = await api.post('/api/tenants', data);
    return unwrapResource<Tenant>(res.data);
  },

  // Email Config
  async getEmailConfig(): Promise<EmailConfig> {
    if (USE_MOCK) return MOCK_EMAIL_CONFIG;
    const res = await api.get('/api/admin/email-config');
    return unwrapResource<EmailConfig>(res.data);
  },
  async updateEmailConfig(data: Partial<EmailConfig>): Promise<EmailConfig> {
    if (USE_MOCK) return { ...MOCK_EMAIL_CONFIG, ...data };
    const res = await api.patch('/api/admin/email-config', data);
    return unwrapResource<EmailConfig>(res.data);
  },
  async sendTestEmail(): Promise<void> {
    if (USE_MOCK) return;
    await api.post('/api/admin/email-config/test');
  },
  async getEmailLog(): Promise<EmailSendLog[]> {
    if (USE_MOCK) return MOCK_EMAIL_LOG;
    const res = await api.get('/api/admin/email-log');
    return unwrapCollection<EmailSendLog>(res.data);
  },

  // VAPI Config
  async getVapiConfig(): Promise<VapiConfig> {
    if (USE_MOCK) return MOCK_VAPI_CONFIG;
    const res = await api.get('/api/admin/vapi-config');
    return unwrapResource<VapiConfig>(res.data);
  },
  async updateVapiConfig(data: Partial<VapiConfig>): Promise<VapiConfig> {
    if (USE_MOCK) return { ...MOCK_VAPI_CONFIG, ...data };
    const res = await api.patch('/api/admin/vapi-config', data);
    return unwrapResource<VapiConfig>(res.data);
  },
  async triggerTestCall(): Promise<void> {
    if (USE_MOCK) return;
    await api.post('/api/admin/vapi-config/test-call');
  },
  async getVapiCalls(): Promise<VapiCallLog[]> {
    if (USE_MOCK) return MOCK_VAPI_CALLS;
    const res = await api.get('/api/admin/vapi-calls');
    return unwrapCollection<VapiCallLog>(res.data);
  },

  // Integrations
  async getIntegrations(): Promise<IntegrationStatus[]> {
    if (USE_MOCK) return MOCK_INTEGRATIONS;
    const res = await api.get('/api/admin/integrations');
    return unwrapCollection<IntegrationStatus>(res.data);
  },
  async testIntegration(provider: IntegrationProvider): Promise<{ success: boolean }> {
    if (USE_MOCK) return { success: true };
    const res = await api.post(`/api/admin/integrations/${provider}/test`);
    return res.data;
  },

  // Sites
  async getSites(): Promise<Site[]> {
    if (USE_MOCK) return MOCK_SITES;
    const res = await api.get('/api/sites');
    return unwrapCollection<Site>(res.data);
  },
  async createSite(data: Partial<Site>): Promise<Site> {
    if (USE_MOCK) return { ...MOCK_SITES[0], ...data, id: `site-${Date.now()}` } as Site;
    const res = await api.post('/api/sites', data);
    return unwrapResource<Site>(res.data);
  },
  async updateSite(id: string, data: Partial<Site>): Promise<Site> {
    if (USE_MOCK) return { ...MOCK_SITES.find(s => s.id === id)!, ...data };
    const res = await api.patch(`/api/sites/${id}`, data);
    return unwrapResource<Site>(res.data);
  },
  async deleteSite(id: string): Promise<void> {
    if (USE_MOCK) return;
    await api.delete(`/api/sites/${id}`);
  },

  // Customers
  async getCustomers(): Promise<Customer[]> {
    if (USE_MOCK) return MOCK_CUSTOMERS;
    const res = await api.get('/api/customers');
    return unwrapCollection<Customer>(res.data);
  },
  async getCustomer(id: string): Promise<Customer> {
    if (USE_MOCK) return MOCK_CUSTOMERS.find(c => c.id === id) ?? MOCK_CUSTOMERS[0];
    const res = await api.get(`/api/customers/${id}`);
    return unwrapResource<Customer>(res.data);
  },
  async createCustomer(data: Partial<Customer>): Promise<Customer> {
    if (USE_MOCK) return { ...MOCK_CUSTOMERS[0], ...data, id: `cust-${Date.now()}` } as Customer;
    const res = await api.post('/api/customers', data);
    return unwrapResource<Customer>(res.data);
  },
  async updateCustomer(id: string, data: Partial<Customer>): Promise<Customer> {
    if (USE_MOCK) return { ...MOCK_CUSTOMERS.find(c => c.id === id)!, ...data };
    const res = await api.patch(`/api/customers/${id}`, data);
    return unwrapResource<Customer>(res.data);
  },
  async deleteCustomer(id: string): Promise<void> {
    if (USE_MOCK) return;
    await api.delete(`/api/customers/${id}`);
  },
};
